import type { QdsContentMode } from '../../types/content.js';
import type { QdsBlockDropTarget } from '../../types/designer.js';

export const QDS_CONTENT_DND_MIME = 'application/x-qds-content-block';

interface QdsContentDragHoverState {
  ownerId: string;
  target: QdsBlockDropTarget;
}

export type QdsContentDragPayload =
  | {
      kind: 'palette-block';
      type: string;
      mode: QdsContentMode;
    }
  | {
      kind: 'document-block';
      blockId: string;
      mode: QdsContentMode;
    };

function getTransfer(event: DragEvent): DataTransfer | null {
  return event.dataTransfer || null;
}

let activeDragPayload: QdsContentDragPayload | null = null;
let activeDragHover: QdsContentDragHoverState | null = null;

function getTypes(transfer: DataTransfer | null): string[] {
  if (!transfer?.types) {
    return [];
  }

  return Array.from(transfer.types);
}

export function hasContentDragPayload(event: DragEvent): boolean {
  return getTypes(getTransfer(event)).includes(QDS_CONTENT_DND_MIME);
}

export function setContentDragPayload(
  event: DragEvent,
  payload: QdsContentDragPayload
): boolean {
  const transfer = getTransfer(event);

  if (!transfer) {
    return false;
  }

  transfer.setData(QDS_CONTENT_DND_MIME, JSON.stringify(payload));
  transfer.setData('text/plain', payload.kind === 'palette-block' ? payload.type : payload.blockId);
  transfer.effectAllowed = payload.kind === 'palette-block' ? 'copy' : 'move';
  activeDragPayload = payload;
  return true;
}

export function getContentDragPayload(event: DragEvent): QdsContentDragPayload | null {
  const transfer = getTransfer(event);

  if (!transfer || !getTypes(transfer).includes(QDS_CONTENT_DND_MIME)) {
    return null;
  }

  try {
    const raw = transfer.getData(QDS_CONTENT_DND_MIME);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<QdsContentDragPayload>;
    if (parsed.kind === 'palette-block' && typeof parsed.type === 'string') {
      return {
        kind: 'palette-block',
        type: parsed.type,
        mode: parsed.mode === 'dashboard' ? 'dashboard' : 'page'
      };
    }

    if (parsed.kind === 'document-block' && typeof parsed.blockId === 'string') {
      return {
        kind: 'document-block',
        blockId: parsed.blockId,
        mode: parsed.mode === 'dashboard' ? 'dashboard' : 'page'
      };
    }
  } catch (error) {
    console.error('Unable to parse content drag payload.', error);
  }

  return null;
}

export function getActiveContentDragPayload(): QdsContentDragPayload | null {
  return activeDragPayload;
}

export function setActiveContentDragHover(ownerId: string, target: QdsBlockDropTarget): void {
  activeDragHover = { ownerId, target };
}

export function getActiveContentDragHover(): QdsContentDragHoverState | null {
  return activeDragHover;
}

export function clearActiveContentDragHover(ownerId?: string): void {
  if (!ownerId || activeDragHover?.ownerId === ownerId) {
    activeDragHover = null;
  }
}

export function clearActiveContentDragState(): void {
  activeDragPayload = null;
  activeDragHover = null;
}
